/**
 * appRemoteConfig.ts
 *
 * The Firebase Remote Config implementation of `RemoteConfigSource`.
 *
 * This is the only module that imports the Firebase SDK. Everything that
 * decides anything - parsing, window arithmetic, redirect rules - lives in
 * `utils/maintenance.ts` and `router/maintenanceGuard.ts`, so this file
 * stays thin vendor glue rather than logic hidden behind an SDK.
 */
import { initializeApp } from 'firebase/app'
import {
  fetchAndActivate,
  getBoolean,
  getRemoteConfig,
  getString,
  setCustomSignals,
} from 'firebase/remote-config'
import type { RemoteConfig } from 'firebase/remote-config'
import {
  type RemoteConfigSource,
  inactiveRemoteConfig,
} from '@/services/remoteConfig'
import {
  type MaintenanceWindow,
  environmentSignal,
  parseMaintenanceWindow,
} from '@/utils/maintenance'

/** Remote Config parameter names, as created in the Firebase console. */
const ENABLED_KEY = 'maintenance_enabled'
const FROM_KEY = 'maintenance_from'
const TO_KEY = 'maintenance_to'

/**
 * The custom signal every fetch is tagged with.
 *
 * dev and prod share one Firebase project, so the only thing separating
 * their maintenance switches is a Remote Config condition on this signal.
 * The value is normalised by environmentSignal rather than taken from
 * `import.meta.env.MODE` directly: Vite reports "development" on the local
 * dev server, which the condition's exact match on "dev" would never catch.
 */
const ENVIRONMENT_SIGNAL = 'environment'

/**
 * How long a fetched config stays fresh, in milliseconds.
 *
 * Must match the store's polling interval: a shorter poll would be thrown
 * away by this cache, and a longer one would waste the freshness.
 */
const CACHE_TTL_MS = 5 * 60 * 1000

/**
 * Builds the Firebase-backed configuration source.
 *
 * Returns the inactive source when the Firebase environment variables are
 * missing, so a developer without a Firebase project - or a misconfigured
 * deploy - gets a working app rather than a permanent maintenance page.
 *
 * @returns The source to register at startup.
 */
export function createFirebaseRemoteConfig(): RemoteConfigSource {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID
  const appId = import.meta.env.VITE_FIREBASE_APP_ID

  if (!apiKey || !projectId || !appId) {
    console.warn(
      'Firebase Remote Config is not configured; maintenance mode is disabled.',
    )

    return inactiveRemoteConfig
  }

  const app = initializeApp({ apiKey, projectId, appId })
  const remoteConfig = getRemoteConfig(app)

  remoteConfig.settings.minimumFetchIntervalMillis = CACHE_TTL_MS
  remoteConfig.defaultConfig = {
    [ENABLED_KEY]: false,
    [FROM_KEY]: '',
    [TO_KEY]: '',
  }

  // Awaited before every fetch rather than fired and forgotten: a fetch that
  // races ahead of the signal is evaluated against the default condition, and
  // would answer with prod's switch on a dev build.
  const signalsReady = setCustomSignals(remoteConfig, {
    [ENVIRONMENT_SIGNAL]: environmentSignal(import.meta.env.MODE),
  })

  /** Fetches and activates, bypassing the cache when force is set. */
  async function fetchFresh(force: boolean): Promise<void> {
    await signalsReady

    remoteConfig.settings.minimumFetchIntervalMillis = force ? 0 : CACHE_TTL_MS
    try {
      await fetchAndActivate(remoteConfig)
    } finally {
      remoteConfig.settings.minimumFetchIntervalMillis = CACHE_TTL_MS
    }
  }

  return {
    async fetchMaintenance(force = false): Promise<MaintenanceWindow> {
      // A forced fetch is the retry button: the user is asking for an answer
      // newer than the cache would give them.
      await fetchFresh(force)

      return readWindow(remoteConfig)
    },
  }
}

// =============================================================================
// Helper Functions
// =============================================================================

/** Reads the three parameters and parses them into a window. */
function readWindow(remoteConfig: RemoteConfig): MaintenanceWindow {
  return parseMaintenanceWindow({
    enabled: getBoolean(remoteConfig, ENABLED_KEY),
    from: getString(remoteConfig, FROM_KEY),
    to: getString(remoteConfig, TO_KEY),
  })
}
